import { useState } from 'react'
import { ArrowDropDown, ArrowRight } from '@mui/icons-material'
import { List, Drawer, ListItem, ListItemText, Collapse, useTheme, IconButton, styled, useMediaQuery } from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import SubAlbumList from './components/subAlbumList'
import { drawerVar } from '../../../../providers/apollo/cache'
import useBucketShareAlbums from '../../hooks/useBucketShareAlbums'
import useBucketShareById from '../../hooks/useBucketShareById'

export interface ShareAlbum {
  bucketId: string
  description?: string | null
  id: string
  title: string
  subAlbumCount?: number | null
}

interface ShareAlbumsDrawerProps {
  albums: ShareAlbum[]
}

const AlbumLink = styled(Link)(({ theme }) => ({
  display: 'block',
  width: '100%',
  fontSize: '1rem',
  '&:hover': { color: theme.palette.primary.main },
  textAlign: 'left',
  textDecoration: 'none',
  color: 'black'
}))

function ShareAlbumsDrawer({ albums }: ShareAlbumsDrawerProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { shareId } = useParams<{ shareId: string }>()
  const { bucketShare } = useBucketShareById()
  const { shouldRenderShareDrawer } = useBucketShareAlbums(bucketShare)
  const [openAlbums, setOpenAlbums] = useState<string[]>([])
  const drawer = useReactiveVar(drawerVar)
  const isDrawerOpen = drawer === 'shareAlbumsDrawer'

  if (!shouldRenderShareDrawer) {
    return null
  }

  return (
    <Drawer
      data-testid="share-albums-drawer"
      onClose={() => drawerVar(null)}
      open={isDrawerOpen}
      variant={isMobile ? 'temporary' : 'permanent'}
      sx={{
        width: 280,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { position: 'static' },
        height: isMobile ? undefined : 'calc(100vh - 65px)',
        overflow: 'auto'
      }}
    >
      <List>
        <ListItem sx={{ display: 'block' }}>
          <AlbumLink to={`/share/${shareId}`}>All Media</AlbumLink>
          <List>
            {albums.map((album) => {
              const isOpen = openAlbums.includes(album.id)
              const hasSubalbums = (album.subAlbumCount || 0) > 0
              const handleToggle = () => {
                if (openAlbums.includes(album.id)) {
                  setOpenAlbums((prev) => prev.filter((open) => open !== album.id))
                } else {
                  setOpenAlbums((prev) => [...prev, album.id])
                }
              }

              const renderArrow = () => {
                if (!hasSubalbums) {
                  return null
                }

                return (
                  <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    sx={{
                      color: 'primary.main',
                      height: '20px',
                      width: '20px',
                      '&:hover': { color: theme.palette.secondary.main, backgroundColor: theme.palette.primary.highlight }
                    }}
                    onClick={handleToggle}
                    size="small"
                    disableRipple
                  >
                    {isOpen ? <ArrowDropDown color="primary" /> : <ArrowRight color="primary" />}
                  </IconButton>
                )
              }

              return (
                <ListItem
                  key={album.id}
                  sx={{ display: 'block', background: 'unset', border: 'unset', marginLeft: hasSubalbums ? '-6px' : undefined }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {renderArrow()}
                    <AlbumLink to={`/share/${shareId}/album/${album.id}`}>
                      <ListItemText primary={album.title} />
                    </AlbumLink>
                  </div>
                  {hasSubalbums && (
                    <Collapse in={isOpen} sx={{ background: theme.palette.primary.highlight }}>
                      <SubAlbumList shareId={shareId} albumId={album.id} />
                    </Collapse>
                  )}
                </ListItem>
              )
            })}
          </List>
        </ListItem>
      </List>
    </Drawer>
  )
}

export default ShareAlbumsDrawer
